import { useMemo, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useEventFilters } from "components/manualReview/contexts/EventFilterContext";
import {
  CONSUMPTION_EVENT_FILTER,
  EVENT_FILTER_VALUES,
  ManualReviewEventFilter,
  MaterialAreaFilter,
  RAILCARS_FILTER,
  ReviewReasonFilter,
} from "components/manualReview/filters/eventFilters";
import { filterPillSx } from "components/manualReview/filters/filterPillSx";
import { MaterialAreaFilterPill } from "components/manualReview/filters/MaterialAreaFilterPill";
import { ReviewReasonFilterPill } from "components/manualReview/filters/ReviewReasonFilterPill";
import { LimitToggle } from "components/manualReview/filters/LimitToggle";
import { useTenantTranslation } from "hooks/formatters";
import { useIsBehindFeatureFlag } from "hooks/useFeatureFlag";
import { useMaterials } from "hooks/useMaterials";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";

const FILTER_TRANSLATION_KEYS: Record<ManualReviewEventFilter, string> = {
  has_comment: "filterHasComment",
  consumption_event: "filterConsumptionEvent",
  railcars: "railcars",
};

const materialAreaFilterSignature = (filter: MaterialAreaFilter): string =>
  [
    filter.material ?? "",
    filter.area ?? "",
    filter.eventType ?? "",
    filter.limitTo ?? "",
    (filter.negated ?? []).join("."),
  ].join("|");

const materialAreaFilterKeys = (filters: MaterialAreaFilter[]): string[] => {
  const signatures = filters.map(materialAreaFilterSignature);
  return signatures.map(
    (signature, position) =>
      `${signature}#${signatures.slice(0, position).filter((other) => other === signature).length}`
  );
};

const reviewReasonFilterKeys = (filters: ReviewReasonFilter[]): string[] => {
  const signatures = filters.map((filter) => filter.reason ?? "");
  return signatures.map(
    (signature, position) =>
      `${signature}#${signatures.slice(0, position).filter((other) => other === signature).length}`
  );
};

export const EventFilterBar = ({
  location,
}: {
  location: string | undefined;
}) => {
  const { t } = useTenantTranslation();
  const {
    filters,
    setFilters,
    consumptionLimitTo,
    setConsumptionLimitTo,
    materialAreaFilters,
    setMaterialAreaFilters,
    reviewReasonFilters,
    setReviewReasonFilters,
  } = useEventFilters();
  const railcarsEnabled = useIsBehindFeatureFlag("rail_car_movements");
  const targetedReviewEnabled = useIsBehindFeatureFlag("targeted_review");
  const { materials } = useMaterials();
  const { available_areas: areas } = useScrapEyeMetadata();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);

  const selectableMaterials = useMemo(
    () =>
      materials?.filter((material) => material.labelling_name !== null) ?? [],
    [materials]
  );

  const availableCategories = EVENT_FILTER_VALUES.filter(
    (value) =>
      (value !== RAILCARS_FILTER || railcarsEnabled) && !filters.includes(value)
  );
  const activeCount =
    filters.length + materialAreaFilters.length + reviewReasonFilters.length;
  const materialAreaKeys = materialAreaFilterKeys(materialAreaFilters);
  const keyedMaterialAreaFilters = materialAreaFilters.map((filter, index) => ({
    filter,
    index,
    key: materialAreaKeys[index],
  }));
  const reviewReasonKeys = reviewReasonFilterKeys(reviewReasonFilters);
  const keyedReviewReasonFilters = reviewReasonFilters.map((filter, index) => ({
    filter,
    index,
    key: reviewReasonKeys[index],
  }));

  const addCategory = (filter: ManualReviewEventFilter) => {
    setFilters((current) => [...current, filter]);
    setAddAnchor(null);
  };
  const removeCategory = (filter: ManualReviewEventFilter) => {
    setFilters((current) => current.filter((value) => value !== filter));
  };
  const addMaterialAreaFilter = () => {
    setMaterialAreaFilters((current) => [
      ...current,
      { material: null, area: null, eventType: null, limitTo: null },
    ]);
    setAddAnchor(null);
  };
  const updateMaterialAreaFilter = (
    index: number,
    updated: MaterialAreaFilter
  ) => {
    setMaterialAreaFilters((current) =>
      current.map((filter, position) => (position === index ? updated : filter))
    );
  };
  const removeMaterialAreaFilter = (index: number) => {
    setMaterialAreaFilters((current) =>
      current.filter((_, position) => position !== index)
    );
  };
  const addReviewReasonFilter = () => {
    setReviewReasonFilters((current) => [...current, { reason: null }]);
    setAddAnchor(null);
  };
  const updateReviewReasonFilter = (
    index: number,
    updated: ReviewReasonFilter
  ) => {
    setReviewReasonFilters((current) =>
      current.map((filter, position) => (position === index ? updated : filter))
    );
  };
  const removeReviewReasonFilter = (index: number) => {
    setReviewReasonFilters((current) =>
      current.filter((_, position) => position !== index)
    );
  };

  return (
    <>
      <Badge badgeContent={activeCount} color="primary">
        <Button
          ref={triggerRef}
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={() => {
            setOpen(true);
          }}
        >
          {t("filterEvents")}
        </Button>
      </Badge>
      <Popover
        anchorEl={triggerRef.current}
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack spacing={1.5} sx={{ p: 2, maxWidth: 560 }}>
          {activeCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t("noFiltersApplied")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {filters.map((filter) => (
                <Box key={filter} sx={filterPillSx}>
                  <Typography variant="body2">
                    {t(FILTER_TRANSLATION_KEYS[filter])}
                  </Typography>
                  {filter === CONSUMPTION_EVENT_FILTER ? (
                    <LimitToggle
                      limitTo={consumptionLimitTo}
                      onChange={setConsumptionLimitTo}
                    />
                  ) : null}
                  <IconButton
                    size="small"
                    aria-label={t("removeFilter")}
                    onClick={() => {
                      removeCategory(filter);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              {keyedMaterialAreaFilters.map(({ filter, index, key }) => (
                <MaterialAreaFilterPill
                  key={key}
                  filter={filter}
                  materials={selectableMaterials}
                  areas={areas}
                  location={location}
                  onChange={(updated) => {
                    updateMaterialAreaFilter(index, updated);
                  }}
                  onRemove={() => {
                    removeMaterialAreaFilter(index);
                  }}
                />
              ))}
              {keyedReviewReasonFilters.map(({ filter, index, key }) => (
                <ReviewReasonFilterPill
                  key={key}
                  filter={filter}
                  onChange={(updated) => {
                    updateReviewReasonFilter(index, updated);
                  }}
                  onRemove={() => {
                    removeReviewReasonFilter(index);
                  }}
                />
              ))}
            </Box>
          )}
          <Box>
            <Chip
              icon={<AddIcon />}
              label={t("addFilter")}
              variant="outlined"
              onClick={(event) => {
                setAddAnchor(event.currentTarget);
              }}
            />
          </Box>
        </Stack>
        <Menu
          anchorEl={addAnchor}
          open={addAnchor !== null}
          onClose={() => {
            setAddAnchor(null);
          }}
        >
          {availableCategories.map((category) => (
            <MenuItem
              key={category}
              onClick={() => {
                addCategory(category);
              }}
            >
              {t(FILTER_TRANSLATION_KEYS[category])}
            </MenuItem>
          ))}
          <MenuItem onClick={addMaterialAreaFilter}>
            {t("materialAreaFilters")}
          </MenuItem>
          {targetedReviewEnabled ? (
            <MenuItem onClick={addReviewReasonFilter}>
              {t("filterReviewReason")}
            </MenuItem>
          ) : null}
        </Menu>
      </Popover>
    </>
  );
};
